import { Add, DeleteOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { get } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { deleteToken, getTokens } from '../../http/TokensApi';
import AssetIcon from '../shared/AssetIcon';
import AddTokenDialog from './dialogs/AddTokenDialog';

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

const DEFAULT_ERROR_MESSAGE = 'An error happened!';

export default function TokensComponent() {
  const [tokens, setTokens] = useState([]);
  const [snackState, setSnackState] = useState({
    open: false,
    message: DEFAULT_ERROR_MESSAGE,
  });
  const [addTokenDialogOpen, setAddTokenDialogOpen] = useState(false);

  const handleSnackStateChange = useCallback((key, value) => {
    setSnackState((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    async function loadTokens() {
      try {
        const { data } = await getTokens();
        setTokens(data);
      } catch (err) {
        handleSnackStateChange(
          'message',
          get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE),
        );
        handleSnackStateChange('open', true);
      }
    }

    loadTokens();
  }, [handleSnackStateChange]);

  const handleTokenAdded = useCallback(
    (token) => {
      setTokens((prev) => [...prev, token]);
    },
    [setTokens],
  );

  const deleteRow = useCallback(
    async (id) => {
      try {
        await deleteToken(id);
        setTokens((prevTokens) =>
          prevTokens.filter((token) => token.id !== id),
        );
      } catch (err) {
        handleSnackStateChange(
          'message',
          get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE),
        );
        handleSnackStateChange('open', true);
      }
    },
    [handleSnackStateChange],
  );

  const columns = [
    {
      field: 'symbol',
      headerName: 'Symbol',
      flex: 1,
      renderCell: ({ row, formattedValue }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssetIcon
            iconUrl={row.iconUrl}
            label={row.symbol || row.name}
            size={24}
          />
          {!isMobile && <span>{formattedValue}</span>}
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 2,
    },
    {
      field: 'decimals',
      headerName: 'Decimals',
      type: 'number',
      flex: 0.8,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 0.8,
      renderCell: ({ id }) => (
        <IconButton
          onClick={() => deleteRow(id)}
          size="small"
          title="Delete token"
        >
          <DeleteOutline />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: SPACING.LG }}>
      <Stack spacing={SPACING.MD}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h5">Tokens</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddTokenDialogOpen(true)}
          >
            Add token
          </Button>
        </Box>

        <Paper sx={{ p: SPACING.MD, backgroundColor: '#ffffff' }}>
          <div style={{ width: '100%' }}>
            <DataGrid
              autoHeight
              rows={tokens}
              columns={columns}
              columnVisibilityModel={{
                name: !isMobile,
                address: !isMobile,
                decimals: !isMobile,
                actions: !isMobile,
              }}
              getRowId={(row) => row.id}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: 'No tokens available' }}
            />
          </div>
        </Paper>
      </Stack>

      <AddTokenDialog
        open={addTokenDialogOpen}
        onClose={() => setAddTokenDialogOpen(false)}
        onTokenAdded={handleTokenAdded}
        setErrMessage={(message) => handleSnackStateChange('message', message)}
        errMessage={snackState.message}
        setSnackOpen={(open) => handleSnackStateChange('open', open)}
      />

      <Snackbar
        open={snackState.open}
        autoHideDuration={6000}
        onClose={() => handleSnackStateChange('open', false)}
      >
        <Alert severity="error">{snackState.message}</Alert>
      </Snackbar>
    </Box>
  );
}
